import React, { useState } from 'react';
import styled from 'styled-components';
import { FiFilter, FiLoader, FiCheckCircle, FiInfo } from 'react-icons/fi';
import apiClient from '../services/api';
import { toast } from 'react-toastify';

const PanelContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
`;

const SectionTitle = styled.h3`
  color: #424242;
  margin-bottom: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Description = styled.p`
  color: #757575;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #424242;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: 'Segoe UI', sans-serif;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #bdbdbd;
  }
`;

const ApplyButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

const ExampleBox = styled.div`
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
`;

const ExampleTitle = styled.div`
  font-weight: 600;
  color: #424242;
  margin-bottom: 10px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ExampleSection = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ExampleSubtitle = styled.div`
  font-weight: 600;
  color: #667eea;
  margin-bottom: 8px;
  font-size: 0.85rem;
`;

const ExampleItem = styled.div`
  color: #616161;
  font-size: 0.85rem;
  padding: 6px 0;
  padding-left: 10px;
  border-left: 3px solid #667eea;
  margin: 5px 0;
`;

const ResultBox = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: ${props => props.$type === 'enrichment' 
    ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' 
    : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'};
  border: 2px solid ${props => props.$type === 'enrichment' ? '#4caf50' : '#2196f3'};
  border-radius: 10px;
`;

const ResultTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$type === 'enrichment' ? '#2e7d32' : '#1565c0'};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
`;

const StatCard = styled.div`
  background: #fff;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.$type === 'enrichment' ? '#2e7d32' : '#1565c0'};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #616161;
  font-weight: 600;
`;

const InfoBox = styled.div`
  background: #fff;
  padding: 12px;
  border-radius: 6px;
  margin-top: 10px;
  font-size: 0.85rem;
  
  strong {
    color: #424242;
  }
  
  code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.8rem;
  }
`;

const RulesPanel = ({ jobId, processingStatus, onRulesApplied }) => {
  const [ruleText, setRuleText] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const handleApplyRules = async () => {
    if (!ruleText.trim()) {
      toast.error('Please enter a rule');
      return;
    }

    setApplying(true);
    
    try {
      console.log('🎯 Applying rules:', ruleText);
      
      const response = await apiClient.post(`/api/v1/apply-rules/${jobId}`, {
        rule_text: ruleText
      });
      
      console.log('✅ Rules applied:', response.data);
      setResult(response.data);
      
      // Call parent callback
      if (onRulesApplied) {
        onRulesApplied(response.data);
      }
      
      // Show appropriate message based on action type
      if (response.data.action === 'enrichment') {
        toast.success(`✅ Enriched ${response.data.enriched_count} postal codes!`);
      } else {
        toast.success(`✅ Filter applied! ${response.data.filtered_count} rows match.`);
      }
    } catch (error) {
      console.error('❌ Rules error:', error);
      toast.error('Failed to apply rules: ' + (error.response?.data?.detail || error.message));
    } finally {
      setApplying(false);
    }
  };

  return (
    <PanelContainer>
      <SectionTitle>
        <FiFilter /> Smart Rules Engine
      </SectionTitle>
      
      <Description>
        Use natural language to either <strong>filter data</strong> or <strong>enrich missing information</strong>.
        The AI will automatically detect what you want to do!
      </Description>

      <InputGroup>
        <Label>Enter Your Rule</Label>
        <TextArea
          value={ruleText}
          onChange={(e) => setRuleText(e.target.value)}
          placeholder="Example: Add postal code for rows with missing postal code using city and country..."
          disabled={applying}
        />
      </InputGroup>

      <ApplyButton onClick={handleApplyRules} disabled={applying || !ruleText.trim()}>
        {applying ? (
          <>
            <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
            Processing...
          </>
        ) : (
          <>
            <FiFilter />
            Apply Rule
          </>
        )}
      </ApplyButton>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <ExampleBox>
        <ExampleTitle>
          <FiInfo /> Example Rules
        </ExampleTitle>
        
        <ExampleSection>
          <ExampleSubtitle>📥 Data Enrichment (Auto-fill missing data):</ExampleSubtitle>
          <ExampleItem>• Add postal code for rows with missing postal code</ExampleItem>
          <ExampleItem>• Fill missing postal codes using city and country</ExampleItem>
          <ExampleItem>• Auto-complete postal code for Berlin addresses</ExampleItem>
          <ExampleItem>• Enrich postal code where it's empty</ExampleItem>
        </ExampleSection>
        
        <ExampleSection>
          <ExampleSubtitle>🔍 Data Filtering (Show specific records):</ExampleSubtitle>
          <ExampleItem>• Filter postal codes 020 01</ExampleItem>
          <ExampleItem>• Show only Berlin addresses</ExampleItem>
          <ExampleItem>• Filter country Germany</ExampleItem>
          <ExampleItem>• Show rows where postal code starts with 90</ExampleItem>
          <ExampleItem>• Filter bad data with missing postal code</ExampleItem>
        </ExampleSection>
      </ExampleBox>

      {result && (
        <ResultBox $type={result.action}>
          <ResultTitle $type={result.action}>
            <FiCheckCircle />
            {result.action === 'enrichment' ? '🌍 Enrichment Results' : '🔍 Filter Results'}
          </ResultTitle>
          
          {result.action === 'enrichment' ? (
            <>
              <ResultStats>
                <StatCard>
                  <StatValue $type="enrichment">{result.enriched_count}</StatValue>
                  <StatLabel>Postal Codes Added</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $type="enrichment">{result.already_complete_count}</StatValue>
                  <StatLabel>Already Complete</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $type="enrichment">{result.failed_count}</StatValue>
                  <StatLabel>Could Not Find</StatLabel>
                </StatCard>
              </ResultStats>
              
              <InfoBox>
                <strong>Applied Rule:</strong> {result.rule_text}
                <br /><br />
                <strong>Action:</strong> Postal code enrichment using GeoNames database
              </InfoBox>
            </>
          ) : (
            <>
              <ResultStats>
                <StatCard>
                  <StatValue $type="filtering">{result.original_count}</StatValue>
                  <StatLabel>Original Rows</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $type="filtering">{result.filtered_count}</StatValue>
                  <StatLabel>Matching Rows</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $type="filtering">{result.rows_removed}</StatValue>
                  <StatLabel>Filtered Out</StatLabel>
                </StatCard>
              </ResultStats>
              
              <InfoBox>
                <strong>Applied Rule:</strong> {result.rule_text}
                <br /><br />
                {result.expression_used && (
                  <>
                    <strong>Generated Expression:</strong> <code>{result.expression_used}</code>
                  </>
                )}
              </InfoBox>
            </>
          )}
        </ResultBox>
      )}
    </PanelContainer>
  );
};

export default RulesPanel;